import React, { useState, useEffect } from 'react';
import './Mines.css';

import animIdle from './mines_animations/mines_idle.gif';
import animExplosion from './mines_animations/mines_explosion.gif';
import animLose from './mines_animations/mines_lose.gif';
import animFlag from './mines_animations/mines_flag.gif';
import animWin from './mines_animations/mines_win.gif';
import minesLogo from './mines_animations/mines_logo.gif';
import ufLogo from './UF_logo.png';

import CrumblePlus from './crumblePlus';

import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { LightBulbIcon } from '@heroicons/react/24/solid';
import { Bars3Icon } from '@heroicons/react/24/solid';

const BOARD_SIZE = 15;
const MINES_COUNT = 30;
//Use around 30


function generateBoard(size, mines, safeX = null, safeY = null) {
  const board = Array(size)
    .fill(null)
    .map(() =>
      Array(size).fill({
        revealed: false,
        mine: false,
        flagged: false,
        adjacent: 0,
      })
    );
  // If safeX and safeY are provided, mark safe zone
  const safeZone = new Set();
  if (safeX !== null && safeY !== null) {
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const nx = safeX + dx, ny = safeY + dy;
        if (nx >= 0 && nx < size && ny >= 0 && ny < size) {
          safeZone.add(`${nx},${ny}`);
        }
      }
    }
  }

  // Place mines, skipping the safe zone
  let placed = 0;
  while (placed < mines) {
    const x = Math.floor(Math.random() * size);
    const y = Math.floor(Math.random() * size);
    if (!board[x][y].mine && !safeZone.has(`${x},${y}`)) {
      board[x][y] = { ...board[x][y], mine: true };
      placed++;
    }
  }

  // Calculate adjacent mines
  const dirs = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],          [0, 1],
    [1, -1], [1, 0], [1, 1],
  ];
  for (let i = 0; i < size; i++) {
    for (let j = 0; j < size; j++) {
      if (board[i][j].mine) continue;
      let count = 0;
      dirs.forEach(([dx, dy]) => {
        const ni = i + dx, nj = j + dy;
        if (
          ni >= 0 &&
          ni < size &&
          nj >= 0 &&
          nj < size &&
          board[ni][nj].mine
        ) {
          count++;
        }
      });
      board[i][j] = { ...board[i][j], adjacent: count };
    }
  }
  return board;
}

function cloneBoard(board) {
  return board.map(row => row.map(cell => ({ ...cell })));
}

function reveal(board, x, y) {
  const size = board.length;
  if (board[x][y].revealed || board[x][y].flagged) return;
  board[x][y].revealed = true;
  if (board[x][y].adjacent === 0 && !board[x][y].mine) {
    const dirs = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],          [0, 1],
      [1, -1], [1, 0], [1, 1],
    ];
    dirs.forEach(([dx, dy]) => {
      const ni = x + dx, nj = y + dy;
      if (
        ni >= 0 &&
        ni < size &&
        nj >= 0 &&
        nj < size &&
        !board[ni][nj].revealed
      ) {
        reveal(board, ni, nj);
      }
    });
  }
}

function checkWin(board, mines) {
  let revealed = 0;
  board.forEach(row =>
    row.forEach(cell => {
      if (cell.revealed) revealed++;
    })
  );
  return revealed === board.length * board.length - mines;
}

export default function Mines() {
  const [showCrumblePlus, setShowCrumblePlus] = useState(false);
  const [board, setBoard] = useState(() => generateBoard(BOARD_SIZE, 0)); // Start with 0 mines
  const [gameOver, setGameOver] = useState(false);
  const [win, setWin] = useState(false);
  const [started, setStarted] = useState(false);
  const [anim, setAnim] = useState(animIdle);
  
  const [darkMode, setDarkMode] = useState(() => {
      const saved = localStorage.getItem('vpDarkMode');
      return saved ? JSON.parse(saved) : false;
    });

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
    setShowPopup(true);
  }, []);

  useEffect(() => {
    if (gameOver && !win) {
      setAnim(animExplosion);
      const timeout = setTimeout(() => {
        setAnim(animLose);
      }, 5000);
      return () => clearTimeout(timeout); // cleanup
    
    } else if (gameOver && win) {
      setAnim(animFlag);
      const timeout = setTimeout(() => {
        setAnim(animWin);
      }, 6666);
      return () => clearTimeout(timeout); // cleanup

    } else if (!gameOver && !started) {
      setAnim(animIdle);
    }
  }, [gameOver, win, started]);

  useEffect(() => {
    if (checkWin(board, MINES_COUNT)) {
      setWin(true);
      setGameOver(true);
    }
  }, [board]);

const handleCellClick = (x, y) => {
  if (gameOver) return;
  if (board[x][y].flagged) return;

  if (!started) {
    // Generate a new board with mines, avoiding the first click and its neighbors
    const newBoard = generateBoard(BOARD_SIZE, MINES_COUNT, x, y);
    reveal(newBoard, x, y);
    setBoard(newBoard);
    setStarted(true);
    return;
  }

  if (board[x][y].mine) {
    setGameOver(true);
    const newBoard = cloneBoard(board);
    newBoard[x][y].revealed = true;
    setBoard(newBoard);
    return;
  }
  const newBoard = cloneBoard(board);
  reveal(newBoard, x, y);
  setBoard(newBoard);
};

  const handleRightClick = (e, x, y) => {
    e.preventDefault();
    if (gameOver || board[x][y].revealed) return;
    const newBoard = cloneBoard(board);
    newBoard[x][y].flagged = !newBoard[x][y].flagged;
    setBoard(newBoard);
  };

  const resetGame = () => {
    setBoard(generateBoard(BOARD_SIZE, 0)); // Start with 0 mines
    setGameOver(false);
    setWin(false);
    setStarted(false);
    
  };
  
  return (

    <>
      {showInfoPopup && (
        <div className="vp-popup-overlay">
          <div className="vp-popup">
            <h2>About Unstoppable Force, The Makers of Crumble!</h2>
            <p>Unstoppable Force is a bold leadership and personal growth movement built to help people like you level up in real life—not just in-game. </p>
            <p>We turn mental resilience, team connection, and self-leadership into your ultimate power-ups. </p>
            <p>If you’re ready to live beyond your limits, you’re in the right place.</p>
            <p> Visit <a href="https://unstoppableforce.co.nz">our website</a> for more information!</p>
            <button onClick={() => setShowInfoPopup(false)} className="vp-play-button">Back to the game!</button>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="vp-popup-overlay">
          <div className="vp-popup">
            <h2>How to Play Mines</h2>
            <ul>
              <li>Click a cell to reveal it.</li>
              <li>Right-click to flag a cell as a mine.</li>
              <li>Reveal all safe cells to win!</li>
              <li>First click is always safe.</li>
            </ul>
            <button onClick={() => setShowPopup(false)} className="vp-play-button">
              Got it!
            </button>
          </div>
        </div>
      )}

      {!showCrumblePlus && (
        <>
          <div className="vp-toolbar">
            <div className="vp-date"></div>
            <div className="mines-title">
              <img src={minesLogo} alt="minesLogo" />
            </div>
            <button
              className="vp-toolbar-hamburger"
              onClick={() => setShowMobileMenu((open) => !open)}
              aria-label="Menu"
            >
              <Bars3Icon style={{ width: 32, height: 32, verticalAlign: 'middle'}} />
            </button>
            <div className="vp-toolbar-buttons">
              <button
                onClick={() => setShowInfoPopup(true)}
                className="vp-toolbar-button"
                title="About Us"
              >
                <img src={ufLogo} alt="UF Logo" style={{ width: 32, height: 32, verticalAlign: 'middle', borderRadius: '6px' }} />
              </button>
              <button
                onClick={() => {
                  setDarkMode((prev) => {
                    localStorage.setItem('vpDarkMode', JSON.stringify(!prev));
                    return !prev;
                  });
                }}
                className="vp-toolbar-button"
                title="Color Scheme"
              >
                <LightBulbIcon style={{ width: 32, height: 32, verticalAlign: 'middle'}} />
              </button>
              <button
                onClick={() => setShowPopup(true)}
                className="vp-toolbar-button"
                title="How to Play"
              >
                <QuestionMarkCircleIcon style={{ width: 32, height: 32, verticalAlign: 'middle'}} />
              </button>
            </div>
            {/* Mobile dropdown */}
            {showMobileMenu && (
              <div className="vp-mobile-menu">
                <button
                  onClick={() => { setShowInfoPopup(true); setShowMobileMenu(false); }}
                  className="vp-toolbar-button"
                  title="Info"
                >
                  <img src={ufLogo} alt="UF Logo" style={{ width: 32, height: 32, verticalAlign: 'middle', borderRadius: '6px' }} />
                  Info
                </button>
                <button
                  onClick={() => {
                    setDarkMode((prev) => {
                      localStorage.setItem('vpDarkMode', JSON.stringify(!prev));
                      return !prev;
                    });
                    setShowMobileMenu(false);
                  }}
                  className="vp-toolbar-button"
                  title="Light/Dark Mode"
                >
                  <LightBulbIcon style={{ width: 32, height: 32, verticalAlign: 'middle'}} />
                  {darkMode ? 'Light' : 'Dark'}
                </button>
                <button
                  onClick={() => { setShowPopup(true); setShowMobileMenu(false); }}
                  className="vp-toolbar-button"
                  title="How to Play"
                >
                  <QuestionMarkCircleIcon style={{ width: 32, height: 32, verticalAlign: 'middle'}} />
                  How to Play
                </button>
              </div>
            )}
          </div>

            <button
              className="vp-play-button"
              style={{ marginTop: 1 }}
              onClick={() => setShowCrumblePlus(true)}
            >
              Back to Crumble+
            </button>

          <button onClick={resetGame}>Restart</button>
          <div style={{ margin: '10px 0' }}>
            {gameOver && win && <span style={{ color: 'green' }}>You Win!</span>}
            {gameOver && !win && <span style={{ color: 'red' }}>Game Over!</span>}
          </div>
          <div className="vp-body-container">
            <div className="vp-image-container">
              <img src={anim} className="vp-stage-image" alt="animation"/>
            </div>
            {/* Move the button here, outside the image container */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${BOARD_SIZE}, 30px)`,
                gap: '2px',
              }}
            >
              {board.map((row, i) =>
                row.map((cell, j) => (
                  <button
                    key={`${i}-${j}`}
                    onClick={() => handleCellClick(i, j)}
                    onContextMenu={e => handleRightClick(e, i, j)}
                    style={{
                      width: 30,
                      height: 30,
                      background: cell.revealed
                        ? cell.mine
                          ? '#e57373'
                          : '#e0e0e0'
                        : '#90caf9',
                      border: '1px solid #1976d2',
                      fontWeight: 'bold',
                      fontSize: 16,
                      cursor: gameOver ? 'default' : 'pointer',
                      color:
                        cell.adjacent === 1
                          ? 'blue'
                          : cell.adjacent === 2
                          ? 'green'
                          : cell.adjacent > 2
                          ? 'red'
                          : 'black',
                    }}
                    disabled={cell.revealed || gameOver}
                  >
                    {cell.revealed
                      ? cell.mine
                        ? '💣'
                        : cell.adjacent > 0
                        ? cell.adjacent
                        : ''
                      : cell.flagged
                      ? '🚩'
                      : ''}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {showCrumblePlus && (
        <>
          <CrumblePlus />
        </>
      )}
    </>
  );
}